/**
 * @fileoverview `ModuleRowList` (WEB-707) — the shared row primitive every
 * Event v2 bespoke module (lineup, agenda, menu, pour list, seat tiers, skill
 * levels) renders into.
 *
 * Five of the six modules are the same object wearing different words: an
 * ordered list of rows, each with an optional lead (a time, a roman numeral,
 * a date), an optional visual (thumb or initials disc), a title, a meta line,
 * and an optional trailing value. Building the row once is what makes the
 * rest of M2 a genuine parallel fan-out instead of six near-duplicate lists.
 *
 * ## Self-gating
 *
 * Every optional part is omitted from the DOM when absent — no empty flex
 * column, no reserved grid track, no stray separator. A title-only row is
 * just the title. A lead-only row (title + lead, no visual/meta/trail) has
 * no trailing column. Callers pass resolved strings; this file never reads a
 * category slug or a lexicon.
 *
 * ## Accessibility
 *
 * Decorative visuals (`thumb` / `initials`) are `aria-hidden`. The row's
 * accessible name is title + meta (an explicit `aria-label` so a lead or
 * trail cannot steal the name). The list is a real `<ul>` / `<li>`.
 *
 * ## Tokens
 *
 * Row gap rides `--evt-rgap` and thumb width rides `--evt-thumb-w` (WEB-702
 * size ladder, scoped to `.event-spine`). Chroma (`catInk` / `catSoft` /
 * `catLine`) is caller-supplied — the same hexes `getCategoryChroma` already
 * resolved for the page — so this primitive stays free of slug checks.
 *
 * ## WEB-750 — row type scale + geometry corrected against the measured design
 *
 * `docs/design-system/event-v2-m2-module-specs.md` §0b/§0b.1/§3.1 measured
 * every text run in this file against `--evt-body` (16px) where the design
 * uses a distinct 10.5–13px mono scale. Corrected here:
 *
 * - **Row padding**: `pb-[var(--evt-rgap)]` on non-last rows only → **`14px 0`
 *   on EVERY row** (this value does not ladder — spec §7).
 * - **Last-row rule KEPT**: the border-bottom is no longer suppressed via
 *   `:not(:last-child)`. The design measured all three rows of a 3-row list
 *   computing the same `border-bottom`. This is the OPPOSITE of
 *   `EventDecisionPanel` (WEB-738), which suppresses its trailing rule via
 *   `[&>*:last-child]:border-b-0` — do not copy that pattern here. A "more"
 *   link, when present, sits below this final rule.
 * - **`lead`**: 16px → **13px**; tracking → **0.04em**; `min-width: 46px`
 *   added (a floor, not a fixed track).
 * - **`title`**: `leading-snug` (1.375) → **1.25**; added `text-wrap: pretty`.
 * - **`meta`**: 16px → **10.5px**; tracking → **0.06em**; `margin-top`
 *   `mt-0.5` (2px) → **3px**; color `--fg-2` → **`--fg-3`**; added
 *   `[font-family:var(--font-mono)]` (previously unset, so it inherited
 *   whatever font the page ambient stack resolved to instead of mono).
 * - **`trail`**: 16px → **11.5px**; tracking → **0.04em** (color `--fg-2`
 *   was already correct).
 * - **Thumb**: `aspect-ratio: 4/3` (→ 96×72) → **fixed 96×64** via the new
 *   `--evt-thumb-h` token (WEB-702 ladder, eventSpine.css); radius `--r-sm`
 *   (6px) → **`--r-md`** (8px); gradient angle 180deg → **140deg**; added a
 *   `1px solid var(--border)` border (previously none).
 *
 * **Caps are applied in CSS, not inherited from the design.** The mockup's
 * `text-transform` computes to `none` on both `lead` and `meta` — its own
 * fixture strings are simply typed in caps already. `uppercase` is applied
 * here anyway (spec §0b.1's explicit recommendation) so real, non-uppercase
 * data renders consistently instead of shouting inconsistently depending on
 * what the source happened to send. `trail` gets no such treatment — the
 * design does not call for it there.
 *
 * **The initials disc is unchanged** — WEB-750 confirmed it already matches
 * the design (40px, 12px mono, `rounded-full`, catSoft/catLine/catInk).
 */

import type { ReactElement } from 'react';
import Image from 'next/image';

import { cn } from '@/components/atoms/primitives/utils';

/**
 * The three chroma values a row actually paints with. Narrower than
 * `CategoryChroma` on purpose: `cat` (the base hue) is not used here, and
 * accepting the full set would invite a slug lookup this file must not do.
 */
export interface ModuleRowChroma {
  catInk: string;
  catSoft: string;
  catLine: string;
}

/**
 * One row in a module list. Optional fields self-gate — omit the field,
 * omit the column. `visual` is a kind tag: thumbs are a `catSoft → --bg-sunken`
 * gradient block (no `src`); initials are a chroma disc; `image` is a real
 * photo (WEB-809 lineup). Do not invent a thumb or initials disc as a
 * stand-in for a missing photo — omit `visual` instead.
 */
export interface ModuleRow {
  id: string;
  /** `'21:30'` | `'III'` | `'MAY 01'` — a time, a roman numeral, a date. */
  lead?: string;
  visual?: { kind: 'thumb' } | { kind: 'initials'; text: string } | { kind: 'image'; src: string };
  title: string;
  /**
   * When set, `title` renders as a real `<a>`. Callers pass a resolvable
   * destination; this primitive does not invent hrefs. Empty/blank self-gates
   * to plain text.
   */
  titleHref?: string;
  meta?: string;
  trail?: string;
}

export interface ModuleRowListProps {
  rows: readonly ModuleRow[];
  /** Caller-resolved category chroma. Never derived in this file. */
  chroma: ModuleRowChroma;
  /** Optional additional class names on the `<ul>`. */
  className?: string;
}

/**
 * Compose the row's accessible name: title, plus meta when present. Lead,
 * trail, and the decorative visual are excluded by construction.
 */
function rowAccessibleName(row: ModuleRow): string {
  const title = row.title.trim();
  const meta = typeof row.meta === 'string' ? row.meta.trim() : '';
  return meta.length > 0 ? `${title} ${meta}` : title;
}

function RowVisual({
  visual,
  chroma,
}: {
  visual: NonNullable<ModuleRow['visual']>;
  chroma: ModuleRowChroma;
}): ReactElement {
  switch (visual.kind) {
    case 'thumb':
      return (
        <div
          aria-hidden="true"
          data-testid="module-row-visual"
          data-visual-kind="thumb"
          // 96×64 fixed at desktop (spec §3.1) — height does NOT ladder, only
          // width does, so the aspect ratio actually changes across
          // breakpoints (3:2 → 11:8 → 1:1). `--r-md` (8px), not `--r-sm`.
          className="h-(--evt-thumb-h) w-(--evt-thumb-w) shrink-0 rounded-ds-md border border-ds-border"
          // style-dynamic: gradient stops come from the caller's category chroma
          style={{
            backgroundImage: `linear-gradient(140deg, ${chroma.catSoft}, var(--bg-sunken))`,
          }}
        />
      );
    case 'initials':
      return (
        <div
          aria-hidden="true"
          data-testid="module-row-visual"
          data-visual-kind="initials"
          className="flex size-10 shrink-0 items-center justify-center rounded-full text-caption font-semibold uppercase tracking-[0.04em] font-mono"
          style={{
            backgroundColor: chroma.catSoft,
            border: `1px solid ${chroma.catLine}`,
            color: chroma.catInk,
          }}
        >
          {visual.text}
        </div>
      );
    case 'image':
      return (
        <Image
          src={visual.src}
          alt=""
          aria-hidden="true"
          data-testid="module-row-visual"
          data-visual-kind="image"
          className="size-10 shrink-0 rounded-full object-cover"
          width={40}
          height={40}
          sizes="40px"
        />
      );
    default: {
      const _exhaustive: never = visual;
      return _exhaustive;
    }
  }
}

/**
 * Title type scale (spec §0b.1) — shared by the plain `<p>` and the linked `<a>`.
 * `leading-*` must follow the `text-(length:…)` size: tailwind-merge drops a
 * line-height that precedes a font-size in the same `cn()` call.
 */
const TITLE_CLASS =
  'm-0 min-w-0 wrap-break-word font-semibold text-pretty text-(length:--evt-row-t) leading-[1.25] text-fg-1';

/**
 * Stretched-link overlay. `relative` on the `<li>` is the containing block;
 * the `::after` covers the whole row so the title is one destination with a
 * row-sized hit area (same pattern as `VenuePanelSnippet`). The `!` on
 * underline utilities beat globals.css's then-unlayered `a { text-decoration:
 * none }` (now layered in styles/base.css, WEB-853).
 */
const TITLE_LINK_CLASS = cn(
  'text-inherit no-underline',
  "after:absolute after:inset-0 after:content-['']",
  'hover:underline! focus-visible:underline!',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-1',
);

/**
 * `ModuleRowList` — a real `<ul>` of self-gating rows. Returns `null` when
 * `rows` is empty so an empty list never lands in the accessibility tree.
 */
export function ModuleRowList({
  rows,
  chroma,
  className,
}: ModuleRowListProps): ReactElement | null {
  if (rows.length === 0) {
    return null;
  }

  return (
    <ul
      className={cn('m-0 flex list-none flex-col p-0', className)}
      data-testid="module-row-list"
      // `list-style: none` strips the implicit list role in Safari; restore it.
      role="list"
    >
      {rows.map((row) => {
        const lead =
          typeof row.lead === 'string' && row.lead.trim().length > 0 ? row.lead.trim() : null;
        const meta =
          typeof row.meta === 'string' && row.meta.trim().length > 0 ? row.meta.trim() : null;
        const trail =
          typeof row.trail === 'string' && row.trail.trim().length > 0 ? row.trail.trim() : null;
        const titleHref =
          typeof row.titleHref === 'string' && row.titleHref.trim().length > 0
            ? row.titleHref.trim()
            : null;
        const accessibleName = rowAccessibleName(row);

        return (
          <li
            key={row.id}
            data-testid="module-row"
            // When the title is a real link, drop the row `aria-label` so
            // assistive tech does not announce the name twice. The link
            // carries the same accessible name (title + meta).
            aria-label={titleHref === null ? accessibleName : undefined}
            className={cn(
              // WEB-750 (spec §0b): the hairline + 14px vertical padding are
              // now UNCONDITIONAL — the design measured the same
              // border-bottom on all three rows of a 3-row list, last row
              // included. This is the opposite of EventDecisionPanel
              // (WEB-738), which suppresses its last child's rule; do not
              // reintroduce a `:not(:last-child)` guard here. A "more" link,
              // when present, sits below this final rule.
              'flex min-w-0 items-center border-b border-ds-border py-[14px] gap-(--evt-rgap)',
              titleHref !== null && 'relative',
            )}
          >
            {lead !== null ? (
              <span
                data-testid="module-row-lead"
                // 13px / 0.04em / uppercase (spec §0b.1 — `min-width: 46px`
                // is a floor, not a fixed track; a wider lead expands it).
                // `uppercase` is applied even though the mockup's own
                // `text-transform` computes to `none` — its fixture content
                // is simply already caps. Real data may not be; see the
                // file-level WEB-750 note for the full rationale.
                className="min-w-[46px] shrink-0 text-small tabular-nums tracking-[0.04em] font-mono"
                style={{ color: chroma.catInk }}
              >
                {lead}
              </span>
            ) : null}

            {row.visual ? <RowVisual visual={row.visual} chroma={chroma} /> : null}

            <div className="min-w-0 flex-1">
              {titleHref !== null ? (
                <a
                  href={titleHref}
                  data-testid="module-row-title"
                  className={cn(TITLE_CLASS, TITLE_LINK_CLASS)}
                  aria-label={accessibleName}
                >
                  {row.title}
                </a>
              ) : (
                <p data-testid="module-row-title" className={TITLE_CLASS}>
                  {row.title}
                </p>
              )}
              {meta !== null ? (
                <p
                  data-testid="module-row-meta"
                  // 11px (spec §0b.1 draws 10.5px; floored by WEB-1178) / 0.06em /
                  // uppercase / --fg-3 — see
                  // the lead's comment above for why `uppercase` is applied
                  // despite the mockup's own CSS computing `none` here.
                  className="m-0 mt-[3px] min-w-0 wrap-break-word text-micro tracking-[0.06em] text-fg-3 font-mono"
                >
                  {meta}
                </p>
              ) : null}
            </div>

            {trail !== null ? (
              // 11.5px / 0.04em (spec §0b.1); color was already --fg-2.
              // No `uppercase` here — unlike lead/meta, the design does not
              // call for it on trail.
              <span
                data-testid="module-row-trail"
                className="shrink-0 text-right text-[11.5px] tabular-nums tracking-[0.04em] text-fg-2 font-mono"
              >
                {trail}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
